/**
 * Calendar panel wrapper — switches between Manual and SelfSchedule calendar modes.
 * Fetches interviewer calendar events and passes them as external events.
 * Exposes CalendarHandle via forwardRef for programmatic navigation (goToDate).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import {
  CalendarEventsFetchError,
  type PrefetchWeeks,
} from '@/api/calendarEvents';
import { useInterviewerSchedulePrefetch } from '@/api/interviewerSchedule';
import { useShowToast } from '@/hooks';
import type { CalendarHandle } from '@kula-ai/components';
import type { ScheduleMode } from '../config/types';
import { isSelfScheduleMode } from '../config/modeClassifiers';
import type { ScheduleSlot } from '../state/useScheduleSharedState';
import type { InterviewKind } from '../availability';
import {
  useAvailableSlots,
  useBusyCalendarEvents,
  useCommonAvailability,
  useCommonAvailabilityRange,
  COMMON_AVAILABILITY_RANGE_WEEKS,
  useMinimumNoticeOverlay,
  useUnavailableHoursOverlay,
} from './hooks';
import { useScheduleContextEmitter } from './scheduleContext';
import { ManualCalendar } from './ManualCalendar';
import { SelfScheduleCalendar } from './SelfScheduleCalendar';
import { NoSlotsAvailablePopover } from './NoSlotsAvailablePopover';
import { MinimumNoticeHoverPopover } from './MinimumNoticeHoverPopover';
import {
  buildCandidateAvailabilityEvents,
  type CandidateAvailabilitySlot,
} from './events';

interface CalendarPanelProps {
  mode: ScheduleMode;
  /** Drives union (1:1) vs intersection (panel) for common availability. */
  interviewKind?: InterviewKind;
  duration: number;
  timezone?: string;
  interviewerIds?: string[];
  meetingRoomIds?: string[];
  /** Canonical room identifiers (emails) — used for the room busy fetch. */
  meetingRoomEmails?: string[];
  /** `email → friendly room label` — powers the busy-event title /
   *  tooltip so users see "Mercedes" not the raw meeting subject. */
  meetingRoomNamesByEmail?: Record<string, string>;
  interviewName?: string;
  /** Edit mode only — the interview's pre-edit name. Used to dedupe
   *  stale /calendar_events entries of THIS interview against the
   *  live bookedSlot so a time/duration change doesn't show the event
   *  twice on the calendar. */
  originalInterviewName?: string;
  candidateName?: string;
  bookedSlot?: ScheduleSlot | null;
  onSlotSelect?: (_slot: { start: string; end: string }) => void;
  onTimezoneChange?: (_tz: string) => void;
  onSlotsChange?: (
    _slots: Array<{
      start: string;
      end: string;
      is_custom?: boolean;
      override_type?:
        | 'none'
        | 'calendar'
        | 'working_hours'
        | 'calendar_working_hours'
        | 'buffer'
        | 'buffer_calendar'
        | 'buffer_working_hours'
        | 'wh_cal_buffer';
      _fromServer?: boolean;
    }>
  ) => void;
  /**
   * Fires whenever the visible week anchor changes (navigation + tz
   * changes). Consumers use this to drive per-week fetches that live
   * outside the calendar (interviewer workload, dropdown week range).
   */
  onVisibleWeekStartChange?: (_iso: string) => void;
  /**
   * Fires with the subset of interviewer IDs whose calendar isn't
   * connected (busy fetch flagged `calendar_available: false`). The
   * Details form uses this to render a warning icon on each affected
   * chip.
   */
  onCalendarAvailabilityChange?: (_disconnectedIds: number[]) => void;
  isEdit?: boolean;
  /** How many ISO weeks to load from the visible week anchor (default 2) */
  prefetchWeeks?: PrefetchWeeks;
  /** Extra weeks beyond `prefetchWeeks` to background-prefetch (next+next navigation) */
  lookaheadPrefetchWeeks?: number;
  /** Calendar events API — forwarded as `ats_job_id` when set */
  atsJobId?: number;
  /**
   * Edit-mode only. Forwarded as `ats_interview_id` to the calendar
   * fetch so the BE flags this interview's own buffer events with
   * `buffer_kind` (pulled out of the busy feed → diagonal clearance zone).
   */
  atsInterviewId?: number;
  /** Hours of lead time required before the earliest bookable slot. */
  minimumNoticeHours?: number;
  /**
   * Ref for self-schedule imperative commands (`applyFindTime`,
   * `clearAll`). Forwarded straight through to `SelfScheduleCalendar`.
   * Ignored for non-self-schedule modes.
   */
  selfScheduleRef?: React.Ref<import('./selfSchedule').SelfScheduleHandle>;
  /**
   * Fires when the set of days that have pipeline availability
   * changes. Drives the form-side `FindTimeField` labels + walker.
   */
  onAvailableDaysChange?: (_dateKeys: string[]) => void;
  /**
   * Self-schedule only. Fires with the inputs the payload mapper needs
   * to compute `is_custom` / `override_type` per slot at save time:
   * raw per-interviewer working hours (multi-week merged) + busy
   * events for the same window. Gated via content-key compare on the
   * receiver side to avoid the loop trap that broke drag-to-move (see
   * `feedback_scheduling_iteration_rules.md` and the comment on
   * `handleAvailableDaysChange` in DetailsStep).
   */
  onScheduleContextChange?: (_ctx: {
    workingHoursByInterviewer: Record<
      string,
      Array<{ start: string; end: string }>
    >;
    busyEvents: ReadonlyArray<{
      id: string;
      start: string;
      end: string;
      interviewerId: string | number;
    }>;
    /**
     * Windows the two feeds above actually cover, accumulated across
     * every week visited this session. A slot outside all of them is
     * left unclassified rather than guessed at — see
     * `scheduleContext/accumulateScheduleContext.ts` (ENG-3571).
     */
    coveredRanges: ReadonlyArray<{ start: string; end: string }>;
    /**
     * `true` once both the busy fetch and the per-interviewer working
     * hours fetch have settled (or no interviewers are selected, in
     * which case there's nothing to classify against). DetailsStep's
     * validate() gates the Save/Next CTA on this so we don't ship
     * `override_type: 'none'` for slots the user dropped on grey OOO
     * / busy events before the queries resolved. See
     * `payload/computeSlotOverride.ts` for the capture flow.
     */
    isReady: boolean;
  }) => void;
  /**
   * Edit-mode seed for self-schedule — flat list of already-committed
   * `{start, end}` ISO ranges. Grouped by date in the panel's timezone
   * before being handed to `SelfScheduleCalendar`. Read once on mount;
   * subsequent changes are ignored (`useReducer` lazy init).
   */
  initialSelfScheduleSlots?: Array<{
    start: string;
    end: string;
    isBooked?: boolean;
    is_custom?: boolean;
    override_type?:
      | 'none'
      | 'calendar'
      | 'working_hours'
      | 'calendar_working_hours'
      | 'buffer'
      | 'buffer_calendar'
      | 'buffer_working_hours'
      | 'wh_cal_buffer';
  }>;
  /**
   * Clearance (minutes) around each generated slot. Self-schedule
   * only; defaults to 0/0 so manual mode is unaffected.
   */
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * Manual mode only (SCH-57). Candidate-collected availability windows
   * from `GET /ats_candidate_availabilities/:id/candidate_slots`. When
   * present, the calendar renders these green windows (segmented around
   * interviewer busy events, side-by-side) INSTEAD of the computed blue
   * interviewer-availability slots — the recruiter books from the
   * candidate's stated availability. Empty/undefined = unchanged manual
   * flow.
   */
  candidateAvailabilitySlots?: ReadonlyArray<CandidateAvailabilitySlot>;
}

function fallbackVisibleWeekStartIso(tz: string): string {
  // Sunday-first week — matches FullCalendar's `firstDay={0}` so the
  // busy-fetch window aligns with what the user sees on the grid.
  return moment().tz(tz).startOf('week').startOf('day').toISOString();
}

export const CalendarPanel = forwardRef<CalendarHandle, CalendarPanelProps>(
  (
    {
      mode,
      interviewKind = 'one_on_one',
      duration,
      timezone,
      interviewerIds = [],
      meetingRoomEmails = [],
      meetingRoomNamesByEmail = {},
      interviewName,
      originalInterviewName,
      candidateName,
      bookedSlot,
      onSlotSelect,
      onTimezoneChange,
      onSlotsChange,
      onVisibleWeekStartChange,
      onCalendarAvailabilityChange,
      isEdit,
      prefetchWeeks = 2,
      lookaheadPrefetchWeeks = 1,
      atsJobId,
      atsInterviewId,
      minimumNoticeHours = 0,
      selfScheduleRef,
      onAvailableDaysChange,
      onScheduleContextChange,
      initialSelfScheduleSlots,
      bufferBeforeMinutes = 0,
      bufferAfterMinutes = 0,
      candidateAvailabilitySlots,
    },
    ref
  ) => {
    const toast = useShowToast();
    const isSelfSchedule = isSelfScheduleMode(mode);

    const resolvedTz =
      timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isLocalTimezone = resolvedTz === 'local';

    const [activeVisibleStartIso, setActiveVisibleStartIso] = useState<
      string | null
    >(null);

    const handleActiveRangeChange = useCallback(
      (range: { start: string; end: string }) => {
        const startIso = moment
          .tz(range.start, resolvedTz)
          .startOf('day')
          .toISOString();
        setActiveVisibleStartIso(startIso);
      },
      [resolvedTz]
    );

    const { visibleWeekStartIso } = useMemo(() => {
      const tz = resolvedTz;
      const visibleStart =
        activeVisibleStartIso ?? fallbackVisibleWeekStartIso(tz);
      const startM = moment(visibleStart).tz(tz).startOf('day');
      return {
        visibleWeekStartIso: startM.toISOString(),
      };
    }, [activeVisibleStartIso, resolvedTz]);

    const visibleWeekEndIso = useMemo(
      () =>
        moment(visibleWeekStartIso).tz(resolvedTz).add(7, 'days').toISOString(),
      [resolvedTz, visibleWeekStartIso]
    );

    useEffect(() => {
      onVisibleWeekStartChange?.(visibleWeekStartIso);
    }, [onVisibleWeekStartChange, visibleWeekStartIso]);

    const numericInterviewerIds = useMemo(
      () =>
        interviewerIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      [interviewerIds]
    );

    const sortedInterviewerIds = useMemo(
      () => [...numericInterviewerIds].sort((a, b) => a - b),
      [numericInterviewerIds]
    );

    // Interviewer + meeting-room busy events, visible-week filter,
    // edit-mode "this interview" dedupe, pastel colors, and algorithm
    // shapes all live in this shared hook. Both manual and self-schedule
    // now consume the same `ManualCalendarExternalEvent[]` feed since
    // both render via FullCalendar directly.
    const {
      calendarExternalEvents,
      busyEventsForAlgorithm,
      isBlockingBusyFetch,
      isRoomBusyBlocking,
      fetchError,
      usersInfoById,
      isFetchingCalendarChunks,
    } = useBusyCalendarEvents({
      interviewerIds: numericInterviewerIds,
      sortedInterviewerIds,
      meetingRoomEmails,
      meetingRoomNamesByEmail,
      visibleWeekStartIso,
      visibleWeekEndIso,
      resolvedTz,
      isLocalTimezone,
      ...(atsJobId !== undefined && atsJobId !== null && atsJobId > 0
        ? { atsJobId }
        : {}),
      ...(atsInterviewId !== undefined &&
      atsInterviewId !== null &&
      atsInterviewId > 0
        ? { atsInterviewId }
        : {}),
      prefetchWeeks,
      lookaheadPrefetchWeeks,
      isEdit,
      bookedSlot,
      interviewName,
      originalInterviewName,
      bufferBeforeMinutes,
      bufferAfterMinutes,
      // Self-schedule needs busy events to span the same multi-week
      // window as its availability feed so the pipeline subtracts
      // conflicts in future weeks too. Manual mode stays visible-week.
      algorithmWindowWeeks: isSelfSchedule
        ? COMMON_AVAILABILITY_RANGE_WEEKS
        : 1,
    });

    const lastDisconnectedKeyRef = useRef<string>('');
    useEffect(() => {
      if (!onCalendarAvailabilityChange) return;
      // Don't emit "calendar not connected" from partial chunk data.
      // The SCH-40 sticky-true fold in `usersInfoById` can only
      // neutralize a transient per-window `calendar_available:false`
      // once every chunk has resolved; emitting while chunks are still
      // fetching surfaces that single bad window as a false positive
      // (the recurring QA "not connected but events showing" — events
      // come from soft cache / a good chunk while only the bad seed
      // chunk has folded). Hold the last emitted value until settled.
      if (isFetchingCalendarChunks) return;
      const disconnected: number[] = [];
      for (const id of sortedInterviewerIds) {
        const info = usersInfoById[String(id)];
        if (info && info.calendar_available === false) disconnected.push(id);
      }
      // `sortedInterviewerIds` gets a fresh reference each render (the
      // parent re-maps ids in-place), so comparing by content avoids a
      // feedback loop where every emit triggers a shared-state update
      // that re-renders CalendarPanel and re-emits.
      const key = disconnected.join(',');
      if (key === lastDisconnectedKeyRef.current) return;
      lastDisconnectedKeyRef.current = key;
      onCalendarAvailabilityChange(disconnected);
    }, [
      onCalendarAvailabilityChange,
      sortedInterviewerIds,
      usersInfoById,
      isFetchingCalendarChunks,
    ]);

    const lastToastKeyRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        !fetchError ||
        fetchError.name === 'AbortError' ||
        fetchError.name === 'CanceledError'
      ) {
        return;
      }
      const key = `${fetchError.name}:${fetchError.message}`;
      if (lastToastKeyRef.current === key) {
        return;
      }
      lastToastKeyRef.current = key;
      const description =
        fetchError instanceof CalendarEventsFetchError
          ? fetchError.userMessage
          : fetchError.message;
      toast({
        title: 'Calendar',
        description,
        status: 'error',
        duration: 8000,
      });
    }, [fetchError, toast]);

    // Overlay spinner while either the interviewer or meeting-room
    // busy fetch is still resolving the visible week. Applies to both
    // manual and self-schedule now that they share the same feed.
    const showBusyEventsLoader =
      !isLocalTimezone &&
      ((numericInterviewerIds.length > 0 && isBlockingBusyFetch) ||
        isRoomBusyBlocking);

    const { availability } = useCommonAvailability({
      weekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      interviewerIds: sortedInterviewerIds,
      interviewKind,
      // Both modes need availability — self-schedule uses it to grey
      // out unavailable hours and respect minimum-notice leadtime.
      enabled: !isLocalTimezone && sortedInterviewerIds.length > 0,
    });

    // Self-schedule needs availability for multiple weeks so the slot
    // count + day checkboxes reflect picks across every week the user
    // might auto-fill (Find Time "next 2 weeks" spans visible + next).
    // Manual mode only cares about the visible week, so it keeps the
    // single-week call above.
    const {
      availability: rangeAvailability,
      scheduleByUserId: rangeScheduleByUserId,
      isLoading: rangeIsLoading,
      weeks: rangeResolvedWeeks,
    } = useCommonAvailabilityRange({
      weekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      interviewerIds: sortedInterviewerIds,
      interviewKind,
      enabled:
        isSelfSchedule && !isLocalTimezone && sortedInterviewerIds.length > 0,
    });

    // Silently warm React Query with the next few weeks of working
    // hours so forward navigation hits a cache instead of the network.
    useInterviewerSchedulePrefetch({
      anchorWeekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      interviewerIds: sortedInterviewerIds,
      prefetchWeeks,
      lookaheadPrefetchWeeks,
      enabled: !isLocalTimezone && sortedInterviewerIds.length > 0,
    });

    // Once the user has picked a slot, hide the "Available" overlay —
    // it's visual noise at that point and matches legacy behaviour.
    const isSlotBooked = Boolean(bookedSlot?.start && bookedSlot?.end);

    // A window is only offered as "Available" when it fits the whole
    // interview. Shorter gaps are dropped by the gap-finder so the
    // hover preview never has to visualise an unbookable slot.
    const { events: availableSlotEvents, slots: availableSlotsRaw } =
      useAvailableSlots({
        availability: isSlotBooked ? [] : availability,
        busyEvents: busyEventsForAlgorithm,
        timezone: resolvedTz,
        minSlotMinutes: duration,
        minimumNoticeHours,
      });

    // Show the "No slots available this week" banner once the gap-
    // finder has settled (availability loaded, no slot booked yet) and
    // produced zero bookable slots. Avoids flashing while busy events
    // are still arriving from the background poll.
    // Candidate-availability flow has its own green windows — the blue
    // gap-finder is suppressed, so its "no slots this week" banner would
    // be a false negative.
    const showNoSlotsPopover =
      !isSelfSchedule &&
      !isSlotBooked &&
      sortedInterviewerIds.length > 0 &&
      availability.length > 0 &&
      !isBlockingBusyFetch &&
      availableSlotsRaw.length === 0 &&
      !(candidateAvailabilitySlots?.length ?? 0);

    const minimumNoticeEvents = useMinimumNoticeOverlay({
      noticeHours: minimumNoticeHours,
      timezone: resolvedTz,
      visibleWeekStartIso,
      visibleWeekEndIso,
    });

    const unavailableHoursEvents = useUnavailableHoursOverlay({
      availability,
      timezone: resolvedTz,
      visibleWeekStartIso,
      visibleWeekEndIso,
      enabled: sortedInterviewerIds.length > 0,
    });

    // Self-schedule only — push the inputs the payload mapper needs to
    // classify each slot's `is_custom` / `override_type` at save time.
    // Gated by content-key so the emit doesn't loop with the parent's
    // setter (same trap that broke drag-to-move; see
    // `feedback_scheduling_iteration_rules.md`).
    //
    // `isReady` flips true once both the busy fetch and the per-
    // interviewer working hours range have settled (or there are no
    // interviewers to classify against). Without this gate, the first
    // emit fires with empty `busyEvents` + empty `workingHoursByInterviewer`
    // and any slot the user drops onto grey OOO / busy bands is
    // classified as `override_type: 'none'` — masking the conflict from
    // the BE filter. Surfaced via QA against send-additional-slots
    // where the wizard hydrates fast + the user clicks Save quickly.
    const isContextReady =
      sortedInterviewerIds.length === 0 ||
      (!isBlockingBusyFetch && !rangeIsLoading);

    // Both feeds above are windowed on the CURRENT visible week, so
    // navigating away drops the data for weeks the user already picked
    // in. The emitter folds every settled window into a session-scoped
    // snapshot instead — the user had to have the week on screen to
    // pick in it, so its data was fetched; we just stop discarding it
    // (ENG-3571). The reset key is what the accumulation is valid for:
    // another interviewer's hours would be plain wrong, and a timezone
    // switch re-aligns week boundaries.
    useScheduleContextEmitter({
      enabled: isSelfSchedule,
      resetKey: `${sortedInterviewerIds.join(',')}|${resolvedTz}`,
      weeks: rangeResolvedWeeks,
      busyEvents: busyEventsForAlgorithm,
      isReady: isContextReady,
      ...(onScheduleContextChange ? { onChange: onScheduleContextChange } : {}),
    });

    // SCH-57 — candidate-collected availability (green). Manual mode
    // only: when the candidate submitted their availability, render
    // those windows directly (segmented around busy events, side-by-side)
    // instead of the computed blue interviewer slots. `availability`
    // (single-week common availability) drives the mutual vs no-mutual
    // distinction; `duration` is the minimum overlap to count as mutual.
    const candidateBusyEvents = useMemo(
      () =>
        calendarExternalEvents
          .filter((e) => e.status !== 'free')
          .map((e) => ({ start: e.start, end: e.end })),
      [calendarExternalEvents]
    );
    const { events: candidateAvailabilityEvents, overlappingBusyKeys } =
      useMemo(() => {
        if (isSelfSchedule || !candidateAvailabilitySlots?.length) {
          return {
            events: [] as EventInput[],
            overlappingBusyKeys: new Set<string>(),
          };
        }
        return buildCandidateAvailabilityEvents({
          rawSlots: candidateAvailabilitySlots,
          candidateName,
          commonAvailability: availability,
          busyEvents: candidateBusyEvents,
          timezone: resolvedTz,
          durationMinutes: duration,
        });
      }, [
        isSelfSchedule,
        candidateAvailabilitySlots,
        candidateName,
        availability,
        candidateBusyEvents,
        resolvedTz,
        duration,
      ]);

    const hasCandidateAvailability = candidateAvailabilityEvents.length > 0;

    // Tag the busy events that overlap candidate availability so the CSS
    // column-split (`busy-event--has-candidate-overlap`) pushes them to
    // the left half, leaving the green window beside them on the right.
    // Only overlapping events are touched — non-overlapping ones keep
    // their default class (interviewer-busy / meeting-room / free).
    const externalEventsForCalendar = useMemo(() => {
      if (!hasCandidateAvailability) return calendarExternalEvents;
      return calendarExternalEvents.map((evt) => {
        if (evt.status === 'free') return evt;
        if (!overlappingBusyKeys.has(`${evt.start}_${evt.end}`)) return evt;
        const base = evt.classNames ?? 'im-interviewer-busy';
        return {
          ...evt,
          classNames: `${base} busy-event--has-candidate-overlap`,
        };
      });
    }, [hasCandidateAvailability, calendarExternalEvents, overlappingBusyKeys]);

    // Available-slot previews only make sense when the user is picking
    // a single booking slot (Manual). Self-schedule offers windows
    // directly, so those pastel pills would just be noise. When the
    // candidate submitted availability, the blue computed slots are
    // suppressed too — the green candidate windows replace them.
    const overlayEvents = useMemo<EventInput[]>(
      () => [
        ...unavailableHoursEvents,
        // Minimum-notice greyed zones are only meaningful for self-schedule
        // modes where the candidate picks from slots. Manual mode has no
        // slot generator so the overlay just confuses the recruiter.
        ...(isSelfSchedule ? minimumNoticeEvents : []),
        ...(isSelfSchedule || hasCandidateAvailability
          ? []
          : availableSlotEvents),
        ...candidateAvailabilityEvents,
      ],
      [
        isSelfSchedule,
        unavailableHoursEvents,
        minimumNoticeEvents,
        availableSlotEvents,
        hasCandidateAvailability,
        candidateAvailabilityEvents,
      ]
    );

    return (
      <Flex w="100%" h="100%" minH={0} flexDir="column" overflow="visible">
        <Box position="relative" w="100%" h="100%" minH={0} flex="1">
          <MinimumNoticeHoverPopover
            noticeHours={isSelfSchedule ? minimumNoticeHours : 0}
          >
            {isSelfSchedule ? (
              <SelfScheduleCalendar
                ref={selfScheduleRef}
                duration={duration}
                timezone={timezone}
                onSlotsChange={onSlotsChange}
                onTimezoneChange={onTimezoneChange}
                onActiveRangeChange={handleActiveRangeChange}
                externalEvents={calendarExternalEvents}
                overlayEvents={overlayEvents}
                availability={rangeAvailability}
                scheduleByUserId={rangeScheduleByUserId}
                busyEventsForAlgorithm={busyEventsForAlgorithm}
                hasInterviewer={sortedInterviewerIds.length > 0}
                interviewerCount={sortedInterviewerIds.length}
                interviewKind={interviewKind}
                interviewName={interviewName}
                minimumNoticeHours={minimumNoticeHours}
                onAvailableDaysChange={onAvailableDaysChange}
                initialSlots={initialSelfScheduleSlots}
                bufferBeforeMinutes={bufferBeforeMinutes}
                bufferAfterMinutes={bufferAfterMinutes}
              />
            ) : (
              <>
                <ManualCalendar
                  ref={ref}
                  duration={duration}
                  timezone={timezone}
                  interviewName={interviewName}
                  candidateName={candidateName}
                  bookedSlot={bookedSlot}
                  externalEvents={externalEventsForCalendar}
                  overlayEvents={overlayEvents}
                  onSlotSelect={onSlotSelect ?? (() => {})}
                  onTimezoneChange={onTimezoneChange}
                  onActiveRangeChange={handleActiveRangeChange}
                  isEdit={isEdit}
                  bufferBeforeMinutes={bufferBeforeMinutes}
                  bufferAfterMinutes={bufferAfterMinutes}
                  hasCandidateAvailability={hasCandidateAvailability}
                />
                <NoSlotsAvailablePopover
                  // Reset the dismissed state whenever the roster or the
                  // visible week changes so the banner can reappear.
                  key={`${visibleWeekStartIso}|${sortedInterviewerIds.join(',')}`}
                  visible={showNoSlotsPopover}
                />
              </>
            )}
          </MinimumNoticeHoverPopover>
          {showBusyEventsLoader ? (
            // Capture pointer events so the user can't click/drag to
            // create slots on top of busy events that haven't arrived
            // yet. `cursor: not-allowed` communicates "blocked right
            // now" — the Spinner already conveys loading, so we don't
            // need the `wait` cursor on top.
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              bg="whiteAlpha.800"
              zIndex={20}
              pointerEvents="auto"
              cursor="not-allowed"
              borderRadius="md"
            >
              <Spinner size="lg" color="secondary.500" thickness="3px" />
            </Flex>
          ) : null}
        </Box>
      </Flex>
    );
  }
);

CalendarPanel.displayName = 'CalendarPanel';
